import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Star, Package, Gavel, ShieldCheck, Calendar, Loader2, ArrowLeft } from "lucide-react";
import { usersAPI } from "../lib/api";
import ListingCard from "../components/ListingCard";
import VerifiedBadge from "../components/VerifiedBadge";

export default function UserProfile() {
  const { uid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    usersAPI
      .public(uid)
      .then((d) => setData(d))
      .catch((e) => setError(e?.response?.data?.detail || "Profilo non disponibile"))
      .finally(() => setLoading(false));
  }, [uid]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#e86a24]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-black text-[#1d3d3a]">Profilo non trovato</h1>
        <p className="text-slate-500 mt-2">{error}</p>
        <Link to="/" className="inline-block mt-6 text-[#e86a24] font-semibold hover:underline">← Torna alla home</Link>
      </div>
    );
  }

  const u = data.user;
  const joinedDate = u.joined_at ? new Date(u.joined_at).toLocaleDateString("it-IT", { month: "long", year: "numeric" }) : null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="user-public-profile">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1d3d3a] mb-4">
        <ArrowLeft className="w-4 h-4" /> Torna alla home
      </Link>

      {/* HEADER CARD */}
      <div className="relative overflow-hidden rounded-3xl border-2 border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-br from-[#1d3d3a] via-[#244c48] to-[#2d5350]" />
        <div className="relative pt-12 pb-6 px-6 flex flex-col md:flex-row md:items-end gap-4">
          <img
            src={u.avatar}
            alt={u.name}
            className="w-24 h-24 rounded-2xl border-4 border-white shadow-md object-cover bg-white"
            data-testid="user-avatar"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-black text-[#1d3d3a]">{u.name}</h1>
              {u.is_verified && <VerifiedBadge size="md" showLabel />}
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-600 flex-wrap">
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{u.castello}</span>
              <span className="inline-flex items-center gap-1 text-amber-600 font-bold">
                <Star className="w-4 h-4 fill-amber-500 stroke-amber-500" />{Number(u.rating || 5).toFixed(1)}
                <span className="text-slate-500 font-normal">({u.reviews_count || 0} recensioni)</span>
              </span>
              {joinedDate && (
                <span className="inline-flex items-center gap-1 text-slate-500"><Calendar className="w-4 h-4" />Iscritto a {joinedDate}</span>
              )}
            </div>
          </div>
        </div>

        {/* STATS BAR */}
        <div className="grid grid-cols-3 border-t border-slate-200 divide-x divide-slate-200">
          <Stat icon={Package} label="Annunci" value={data.stats.listings_total} color="text-[#e86a24]" />
          <Stat icon={Star} label="In Vetrina" value={data.stats.listings_featured} color="text-amber-500" />
          <Stat icon={Gavel} label="Aste attive" value={data.stats.auctions_active} color="text-[#1d3d3a]" />
        </div>
      </div>

      {/* TRUST BANNER */}
      {u.is_verified ? (
        <div className="mt-4 bg-sky-50 border border-sky-200 rounded-2xl p-4 flex items-start gap-3" data-testid="trust-banner-verified">
          <ShieldCheck className="w-6 h-6 text-sky-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-sky-900">Venditore verificato</div>
            <div className="text-sm text-sky-800">Residenza in Repubblica di San Marino confermata dal nostro team. Puoi acquistare con maggiore tranquillità.</div>
          </div>
        </div>
      ) : (
        <div className="mt-4 bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm text-slate-600">
          Questo profilo non ha ancora completato la verifica della residenza. Procedi con prudenza nelle trattative.
        </div>
      )}

      {/* LISTINGS */}
      <div className="mt-8">
        <h2 className="text-xl font-black text-[#1d3d3a] mb-3">
          Annunci di {u.name} {data.listings.length > 0 && <span className="text-slate-400 font-normal text-base">({data.listings.length})</span>}
        </h2>
        {data.listings.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400">
            <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
            Nessun annuncio attivo al momento
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.listings.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="py-4 px-3 text-center">
      <Icon className={`w-5 h-5 mx-auto mb-1 ${color}`} />
      <div className="text-2xl font-black text-[#1d3d3a]">{value}</div>
      <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">{label}</div>
    </div>
  );
}
